import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CommandIds,
  CommandIdsWithParameters,
  fetchContentAssistanceGeneratedContent,
  sendFeedbackToML,
  NormalizedContentAssistanceResult,
} from '../api/contentAssistanceApi';
import { UsageTracker } from '../utils/usageTracker';
import {
  ERROR,
  FetchStatus,
  FINISHED,
  NOT_STARTED,
  STARTED,
} from './constants';
import { useUsageTracker } from './useUsageTracker';
import { useHttpClient } from './useHttpClient';

type $tsMigrateAny = any;

export type UseFetchProps = {
  onSuccess: (result: NormalizedContentAssistanceResult[]) => void;
  onError: (error: Error) => void;
  language?: string; // Language code, e.g. `es-es`
  objectId?: string;
  applicationId?: string;
  basePath?: string; // overrides default CA execution endpoints for POST & GET polling
  pollingInterval?: number; // wait between GET polling requests, in milliseconds
  maxPollingAttempts?: number; // max GET polling requests before ceasing & rejecting
};

// Used by internal "off the shelf" components
export const useFetchContentAssistanceContent = ({
  onSuccess,
  onError,
  language,
  objectId,
  applicationId,
  basePath,
  pollingInterval,
  maxPollingAttempts,
}: UseFetchProps) => {
  const [result, setResult] = useState<
    NormalizedContentAssistanceResult[] | null
  >(null);
  const [error, setError] = useState(null);
  const [request, setRequest] = useState<FetchStatus>(NOT_STARTED);
  const http = useHttpClient();

  const getGeneratedContent = useCallback(
    <
      CommandId extends CommandIds,
      Parameters extends CommandIdsWithParameters[CommandId],
    >({
      commandId,
      featureId,
      parameters,
      resultCount,
      onSuccess: _onSuccess,
      onError: _onError,
      signal,
    }: {
      commandId: CommandId | $tsMigrateAny;
      featureId?: number;
      parameters: Parameters | $tsMigrateAny;
      resultCount?: number;
      onSuccess?: Function;
      onError?: Function;
      signal?: AbortSignal;
    }) => {
      const successHandler = _onSuccess || onSuccess;
      const errorHandler = _onError || onError;
      setRequest(STARTED);
      fetchContentAssistanceGeneratedContent({
        http,
        commandId,
        featureId,
        parameters,
        resultCount,
        language,
        objectId,
        applicationId,
        basePath,
        pollingInterval,
        maxPollingAttempts,
        signal,
      })
        .then((response) => {
          setResult(response);
          successHandler(response);
          setRequest(FINISHED);
        })
        .catch((e) => {
          setRequest(ERROR);
          setError(e);
          errorHandler(e);
        });
    },
    [
      http,
      language,
      objectId,
      applicationId,
      onSuccess,
      onError,
      basePath,
      maxPollingAttempts,
      pollingInterval,
    ]
  );
  const reset = useCallback(() => {
    setResult([]);
    setRequest(NOT_STARTED);
    setError(null);
  }, []);

  const sendFeedback = useCallback(
    (feedbackAnnotatedCompletions: NormalizedContentAssistanceResult[]) => {
      sendFeedbackToML({
        http,
        feedbackAnnotatedCompletions,
      }).catch(() => {
        console.warn('Error sending feedback to ML');
      });
    },
    [http]
  );

  return {
    request,
    error,
    result,
    reset,
    sendFeedback,
    fetch: getGeneratedContent,
  };
};

const ALLOWED_ORIGIN_SUFFIXES = [
  '.hubspot.com',
  '.hubspotqa.com',
  // CMS Domains
  '.hubspotpreviewqa-na1.com',
  '.hubspotpreviewqa-eu1.com',
  '.hubspotpreview-na1.com',
  '.hubspotpreview-eu1.com',
];

const messageIsFromValidDomain = (evt: MessageEvent) => {
  try {
    const { protocol, hostname } = new URL(evt.origin);
    if (protocol !== 'https:') {
      return false;
    }
    return ALLOWED_ORIGIN_SUFFIXES.some(
      (suffix) => hostname === suffix.slice(1) || hostname.endsWith(suffix)
    );
  } catch {
    return false;
  }
};
// A hook for use in a parent app that lives on the app.hubspot.com domain, it listens for post messages from other domains (CMS Editors is the primary use case, as the preview iframes are on portal specific domains)
export const useDelegatedFetchGeneratedContent = (
  childFrameName: string,
  usageTracker: UsageTracker
) => {
  const trackUsage = useUsageTracker({
    usageTracker,
    usageDescription: 'embeded-generation',
  });
  const http = useHttpClient();

  // Requesting completion
  useEffect(() => {
    // Listen for requests
    const listener = (event: MessageEvent) => {
      if (
        messageIsFromValidDomain(event) &&
        event.data.type === 'CONTENT_ASSISTANCE_DELEGATED_REQUEST'
      ) {
        const requestOrigin = event.origin;
        const {
          commandId,
          featureId,
          parameters,
          resultCount,
          language,
          objectId,
          applicationId,
          maxPollingAttempts,
          pollingInterval,
        } = event.data;
        fetchContentAssistanceGeneratedContent({
          http,
          commandId,
          featureId,
          parameters,
          resultCount,
          language,
          objectId,
          applicationId,
          maxPollingAttempts,
          pollingInterval,
        })
          .then((results: any) => {
            (window.frames as any)[childFrameName].postMessage(
              {
                type: 'CONTENT_ASSISTANCE_DELEGATED_RESPONSE',
                results,
              },
              requestOrigin
            );
          })
          .catch((error) => {
            (window.frames as any)[childFrameName].postMessage(
              {
                type: 'CONTENT_ASSISTANCE_DELEGATED_ERROR',
                error,
              },
              requestOrigin
            );
          });
      }
    };
    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
  }, [http, childFrameName, trackUsage]);

  // Sending feedback to ML
  useEffect(() => {
    // Listen for requests
    const listener = (event: MessageEvent) => {
      if (
        messageIsFromValidDomain(event) &&
        event.data.type === 'CONTENT_ASSISTANCE_DELEGATED_FEEDBACK'
      ) {
        const { feedbackAnnotatedCompletions } = event.data;
        sendFeedbackToML({
          http,
          feedbackAnnotatedCompletions,
        }).catch(() => {
          console.warn('Error sending feedback to ML');
        });
      }
    };
    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
  }, [http, childFrameName, trackUsage]);
};

const useSetupFetchGeneratedContent = () => {
  const resolveRef = useRef<Function | null>(undefined);
  const errorRef = useRef<Function | null>(undefined);
  // Send requests to parent frame
  const fetchGeneratedContent = <
    CommandId extends CommandIds,
    Parameters extends CommandIdsWithParameters[CommandId],
  >({
    commandId,
    parameters,
    resultCount = 1,
    language,
    objectId,
    applicationId,
    pollingInterval,
    maxPollingAttempts,
  }: {
    commandId: string;
    parameters: Parameters;
    resultCount?: number;
    language?: string;
    objectId?: string;
    applicationId?: string;
    pollingInterval?: number;
    maxPollingAttempts?: number;
  }) => {
    const completionPromise = new Promise<NormalizedContentAssistanceResult[]>(
      (resolve, error) => {
        resolveRef.current = resolve;
        errorRef.current = error;
      }
    );
    // todo make this not be a wild card
    window.parent.postMessage(
      {
        type: 'CONTENT_ASSISTANCE_DELEGATED_REQUEST',
        commandId,
        parameters,
        resultCount,
        language,
        objectId,
        applicationId,
        pollingInterval,
        maxPollingAttempts,
      },
      '*'
    );
    return completionPromise;
  };

  const sendCompletionFeedback = (
    feedbackAnnotatedCompletions: NormalizedContentAssistanceResult[]
  ) => {
    window.parent.postMessage(
      {
        type: 'CONTENT_ASSISTANCE_DELEGATED_FEEDBACK',
        feedbackAnnotatedCompletions,
      },
      '*'
    );
  };

  // Listen to responses
  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (!messageIsFromValidDomain(event)) {
        return;
      }
      if (event.data.type === 'CONTENT_ASSISTANCE_DELEGATED_RESPONSE') {
        if (resolveRef.current) {
          resolveRef.current(
            event.data.results as NormalizedContentAssistanceResult[]
          );
        }
      }
      if (event.data.type === 'CONTENT_ASSISTANCE_DELEGATED_ERROR') {
        if (errorRef.current) {
          errorRef.current(event.data.error);
        }
      }
    };

    window.addEventListener('message', listener);

    return () => {
      window.removeEventListener('message', listener);
    };
  }, []);

  return { fetchGeneratedContent, sendCompletionFeedback };
};

export type UseProxiedFetchProps = Omit<UseFetchProps, 'basePath'>;

export const useProxiedFetchGeneratedContent = ({
  onSuccess,
  onError,
  language,
  objectId,
  applicationId,
  pollingInterval,
  maxPollingAttempts,
}: UseProxiedFetchProps) => {
  const [result, setResult] = useState<
    NormalizedContentAssistanceResult[] | null
  >(null);
  const [error, setError] = useState(null);
  const [request, setRequest] = useState(NOT_STARTED);
  const { fetchGeneratedContent, sendCompletionFeedback } =
    useSetupFetchGeneratedContent();
  const fetchCommand = useCallback(
    <
      CommandId extends CommandIds,
      Parameters extends CommandIdsWithParameters[CommandId],
    >({
      commandId,
      parameters,
      resultCount,
    }: {
      commandId: string;
      parameters: Parameters;
      resultCount?: number;
    }) => {
      setRequest(STARTED);
      fetchGeneratedContent({
        commandId,
        parameters,
        resultCount,
        language,
        objectId,
        applicationId,
        pollingInterval,
        maxPollingAttempts,
      })
        .then((response) => {
          setResult(response);
          onSuccess(response);
          setRequest(FINISHED);
        })
        .catch((e) => {
          setRequest(ERROR);
          setError(e);
          onError(e);
        });
    },
    [
      fetchGeneratedContent,
      language,
      objectId,
      applicationId,
      onSuccess,
      onError,
      pollingInterval,
      maxPollingAttempts,
    ]
  );
  const reset = useCallback(() => {
    setResult([]);
    setRequest(NOT_STARTED);
    setError(null);
  }, []);

  const sendFeedback = (feedback: NormalizedContentAssistanceResult[]) => {
    sendCompletionFeedback(feedback);
  };

  return {
    request,
    error,
    result,
    reset,
    sendFeedback,
    fetch: fetchCommand,
  };
};
